// @ts-nocheck
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';

import { useWalletSessionMaybe } from './WalletSessionContext';
import {
  deleteTabletJob as apiDeleteTabletJob,
  fetchTabletJobs,
  migrateTabletJobs,
  upsertTabletJob,
} from '../util/api';
import { isUserAuthorized } from '../util/userHelpers';
import {
  JOB_STATUS_LABELS,
  MATCH_WINDOW_MS,
  ESCROW_STATUS_LABELS,
  buildDemoQuotes,
  buildStaggeredQuotePlan,
  canTransition,
  generateHandoffPin,
  newEntityId,
} from '../util/jobLifecycle';

const STORAGE_KEY = 'bx_job_board';
const MIGRATED_FLAG_KEY = 'bx_job_board_migrated_v1';
const SYNC_DEBOUNCE_MS = 800;

const readStored = () => {
  if (typeof window === 'undefined') {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStored = jobs => {
  if (typeof window === 'undefined') {
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(jobs));
};

const jobUpdatedMs = job => {
  const t = Date.parse(job?.updatedAt || job?.createdAt || 0);
  return Number.isFinite(t) ? t : 0;
};

/** Prefer newer updatedAt; remote wins on equal. */
const mergeJobLists = (localJobs, remoteJobs) => {
  const map = new Map();
  (localJobs || []).forEach(j => {
    if (j?.id) map.set(j.id, j);
  });
  (remoteJobs || []).forEach(remote => {
    if (!remote?.id) return;
    const local = map.get(remote.id);
    if (!local || jobUpdatedMs(remote) >= jobUpdatedMs(local)) {
      map.set(remote.id, { ...remote, persisted: true });
    }
  });
  return Array.from(map.values()).sort((a, b) => jobUpdatedMs(b) - jobUpdatedMs(a));
};

const systemMessage = body => ({
  id: newEntityId('msg'),
  author: 'system',
  body,
  createdAt: new Date().toISOString(),
});

const JobBoardContext = createContext({
  jobs: [],
  syncStatus: 'local',
  syncReady: false,
  createJob: () => null,
  getJob: () => undefined,
  listJobs: () => [],
  attachQuotes: () => {},
  acceptQuote: () => {},
  claimQuote: () => false,
  setSchedule: () => {},
  transitionJob: () => false,
  configureEscrow: () => {},
  lockEscrow: () => false,
  releaseEscrow: () => false,
  refundEscrow: () => false,
  addMessage: () => {},
  cancelJob: () => false,
  openDispute: () => false,
  closeMatchWindow: () => {},
  flagConcierge: () => {},
  removeJob: () => {},
  statusLabel: s => JOB_STATUS_LABELS[s] || s,
  escrowLabel: s => ESCROW_STATUS_LABELS[s] || s,
});

export const JobBoardProvider = ({ children }) => {
  const isAuthenticated = useSelector(state => Boolean(state.auth?.isAuthenticated));
  const currentUser = useSelector(state => state.user?.currentUser);
  const walletSession = useWalletSessionMaybe();
  const web2Ready = Boolean(
    isAuthenticated && currentUser?.id?.uuid && isUserAuthorized(currentUser)
  );
  const web3Ready = Boolean(walletSession?.isWalletAuthenticated && walletSession?.walletAddress);
  /** Soft sync identity: Sharetribe account OR wallet session (crypto-first). */
  const syncReady = web2Ready || web3Ready;
  const ownerId = web2Ready
    ? currentUser.id.uuid
    : web3Ready
      ? `web3:${String(walletSession.walletAddress).toLowerCase()}`
      : null;

  const [jobs, setJobs] = useState(readStored);
  const [syncStatus, setSyncStatus] = useState('local');
  const jobsRef = useRef(jobs);
  const dirtyIdsRef = useRef(new Set());
  const skipNextPersistRef = useRef(false);
  const hydrateGenRef = useRef(0);

  useEffect(() => {
    jobsRef.current = jobs;
  }, [jobs]);

  useEffect(() => {
    writeStored(jobs);
  }, [jobs]);

  // Hydrate from shadow API when Sharetribe session is ready; one-shot migrate local jobs.
  useEffect(() => {
    if (typeof window === 'undefined' || !ownerId) {
      setSyncStatus('local');
      return undefined;
    }

    const gen = ++hydrateGenRef.current;
    let cancelled = false;
    setSyncStatus('hydrating');

    (async () => {
      try {
        const { jobs: remote = [] } = await fetchTabletJobs();
        if (cancelled || gen !== hydrateGenRef.current) {
          return;
        }

        const local = readStored();
        const merged = mergeJobLists(local, remote);
        skipNextPersistRef.current = true;
        setJobs(merged);
        writeStored(merged);

        const migrateKey = `${MIGRATED_FLAG_KEY}:${ownerId}`;
        const alreadyMigrated = window.localStorage.getItem(migrateKey) === '1';
        const localOnly = local.filter(j => j?.id && !remote.some(r => r.id === j.id));
        if (!alreadyMigrated && localOnly.length) {
          try {
            await migrateTabletJobs(localOnly);
            window.localStorage.setItem(migrateKey, '1');
          } catch (migrateErr) {
            console.warn('tablet job migrate failed', migrateErr);
          }
        } else if (!alreadyMigrated) {
          window.localStorage.setItem(migrateKey, '1');
        }

        if (!cancelled && gen === hydrateGenRef.current) {
          setSyncStatus('synced');
        }
      } catch (err) {
        if (!cancelled && gen === hydrateGenRef.current) {
          console.warn('tablet job hydrate failed — staying on local cache', err);
          setSyncStatus(err?.status === 401 ? 'local' : 'error');
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [ownerId]);

  const markDirty = useCallback(id => {
    if (id) {
      dirtyIdsRef.current.add(id);
    }
  }, []);

  // Debounced upsert of dirty jobs while authenticated.
  useEffect(() => {
    if (!ownerId || syncStatus === 'hydrating') {
      return undefined;
    }
    if (skipNextPersistRef.current) {
      skipNextPersistRef.current = false;
      return undefined;
    }
    if (!dirtyIdsRef.current.size) {
      return undefined;
    }

    const timer = setTimeout(() => {
      const dirty = Array.from(dirtyIdsRef.current);
      dirtyIdsRef.current.clear();
      if (!dirty.length || !ownerId) {
        return;
      }
      const snapshot = jobsRef.current;
      Promise.all(
        dirty.map(id => {
          const job = snapshot.find(j => j.id === id);
          if (!job) {
            return Promise.resolve();
          }
          return upsertTabletJob(job).catch(err => {
            console.warn('tablet job upsert failed', id, err);
            dirtyIdsRef.current.add(id);
            setSyncStatus('error');
          });
        })
      ).then(() => {
        if (!dirtyIdsRef.current.size) {
          setSyncStatus(prev => (prev === 'error' ? prev : 'synced'));
        }
      });
    }, SYNC_DEBOUNCE_MS);

    return () => clearTimeout(timer);
  }, [jobs, ownerId, syncStatus]);

  // Tick match windows: stagger demo quotes; close when expired.
  useEffect(() => {
    const timer = setInterval(() => {
      const now = Date.now();
      setJobs(prev => {
        let changed = false;
        const dirtyTick = [];
        const next = prev.map(j => {
          if (j.status !== 'matching' || !j.match) {
            return j;
          }

          const createdMs = new Date(j.createdAt).getTime();
          let quotes = [...(j.quotes || [])];
          let messages = [...(j.messages || [])];
          let status = j.status;
          let acceptedQuoteId = j.acceptedQuoteId;
          let providerId = j.providerId;
          let providerName = j.providerName;
          const match = {
            ...j.match,
            plan: (j.match.plan || []).map(step => ({ ...step })),
          };

          match.plan.forEach(step => {
            if (step.delivered || acceptedQuoteId) {
              return;
            }
            if (now < createdMs + step.delayMs) {
              return;
            }
            step.delivered = true;
            changed = true;
            const [q] = buildDemoQuotes([step.provider], {
              brief: j.brief,
              budget: j.brief?.budget || j.brief?.cashBudget,
              currency: j.brief?.currency,
            });
            if (!q) {
              return;
            }
            quotes.push(q);
            messages.push(systemMessage(`Quote in from ${q.providerName}.`));
            if (match.mode === 'first_claim' && !match.autoClaimed) {
              match.autoClaimed = true;
              acceptedQuoteId = q.id;
              providerId = q.providerId;
              providerName = q.providerName;
              status = 'accepted';
              messages.push(
                systemMessage(
                  `First claim: ${q.providerName} locked at ${q.amount} ${q.currency}.`
                )
              );
            }
          });

          if (!acceptedQuoteId && status === 'matching' && quotes.length) {
            status = 'quoted';
            changed = true;
          }

          if (match.endsAt && now >= new Date(match.endsAt).getTime()) {
            if (status === 'matching') {
              status = quotes.length ? 'quoted' : 'open';
              messages.push(systemMessage('Match window closed.'));
              match.closedNotified = true;
              changed = true;
            } else if (status === 'quoted' && !match.closedNotified) {
              match.closedNotified = true;
              messages.push(systemMessage('Match window closed — pick a quote or cancel.'));
              changed = true;
            }
          }

          if (!changed) {
            return j;
          }

          dirtyTick.push(j.id);
          return {
            ...j,
            status,
            quotes,
            messages,
            match,
            acceptedQuoteId,
            providerId,
            providerName,
            updatedAt: new Date().toISOString(),
          };
        });
        if (changed) {
          dirtyTick.forEach(id => dirtyIdsRef.current.add(id));
        }
        return changed ? next : prev;
      });
    }, 1500);
    return () => clearInterval(timer);
  }, []);

  const createJob = useCallback(
    ({
      appId,
      kind = 'services',
      title,
      brief = {},
      scheduleWindow = '',
      status = 'matching',
      demoProviders = [],
      autoQuotes = true,
      matchMode = 'best_quote',
    }) => {
      const now = new Date().toISOString();
      const id = newEntityId('job');
      const useMatch = autoQuotes && demoProviders.length;
      const plan = useMatch ? buildStaggeredQuotePlan(demoProviders, brief) : [];
      const immediateQuotes =
        useMatch && matchMode === 'best_quote'
          ? [] // arrive via stagger
          : useMatch
            ? []
            : [];

      const wantEscrow =
        brief.useEscrow === 'yes' ||
        brief.useEscrow === true ||
        (Array.isArray(brief.settlementMethods) &&
          brief.settlementMethods.includes('locked_escrow')) ||
        brief.settlementMethods?.locked_escrow;

      const escrowAmount =
        String(brief.escrowAmount || brief.budget || brief.cashBudget || brief.deposit || '').trim() ||
        '';

      const entry = {
        id,
        appId,
        kind,
        title,
        status: useMatch ? 'matching' : status === 'matching' ? 'open' : status,
        brief,
        scheduleWindow,
        quotes: immediateQuotes,
        acceptedQuoteId: null,
        providerId: null,
        providerName: null,
        match: useMatch
          ? {
              mode: matchMode,
              endsAt: new Date(Date.now() + MATCH_WINDOW_MS).toISOString(),
              plan: plan.map(p => ({ ...p, delivered: false })),
              eligibleCount: plan.length,
              closedNotified: false,
              autoClaimed: false,
            }
          : null,
        escrow: {
          status: wantEscrow ? 'offered' : 'none',
          milestone: brief.escrowMilestone || 'deposit',
          amount: escrowAmount,
          currency: brief.currency || brief.escrowCurrency || 'JMD',
          pin: null,
          lockedAt: null,
          releasedAt: null,
          releaseMethod: null,
        },
        cancel: null,
        dispute: null,
        messages: [
          systemMessage('Request posted.'),
          ...(useMatch
            ? [
                systemMessage(
                  `Smart match started · ${plan.length} eligible · ${
                    matchMode === 'first_claim' ? 'first claim wins' : 'best quote window'
                  } (${Math.round(MATCH_WINDOW_MS / 1000)}s).`
                ),
              ]
            : []),
          ...(wantEscrow
            ? [
                systemMessage(
                  `Escrow offered (${brief.escrowMilestone || 'deposit'})` +
                    (escrowAmount ? ` · ${escrowAmount} ${brief.currency || 'JMD'}` : '') +
                    ' — lock after accept (non-custodial demo).'
                ),
              ]
            : []),
        ],
        createdAt: now,
        updatedAt: now,
      };
      setJobs(prev => [entry, ...prev]);
      markDirty(id);
      return entry;
    },
    [markDirty]
  );

  const getJob = useCallback(id => jobs.find(j => j.id === id), [jobs]);

  const listJobs = useCallback(
    ({ appId, kind } = {}) =>
      jobs.filter(j => {
        if (appId && j.appId !== appId) {
          return false;
        }
        if (kind && j.kind !== kind) {
          return false;
        }
        return true;
      }),
    [jobs]
  );

  const patchJob = useCallback(
    (id, updater) => {
      setJobs(prev =>
        prev.map(j => {
          if (j.id !== id) {
            return j;
          }
          const next = typeof updater === 'function' ? updater(j) : { ...j, ...updater };
          return { ...next, updatedAt: new Date().toISOString() };
        })
      );
      markDirty(id);
    },
    [markDirty]
  );

  const attachQuotes = useCallback(
    (jobId, quotes) => {
      patchJob(jobId, j => ({
        ...j,
        quotes: [...(j.quotes || []), ...quotes],
        status:
          j.status === 'matching' || j.status === 'open' || canTransition(j.status, 'quoted')
            ? 'quoted'
            : j.status,
        messages: [
          ...(j.messages || []),
          systemMessage(`${quotes.length} new quote${quotes.length === 1 ? '' : 's'} added.`),
        ],
      }));
    },
    [patchJob]
  );

  const acceptQuote = useCallback(
    (jobId, quoteId) => {
      patchJob(jobId, j => {
        if (j.acceptedQuoteId) {
          return j;
        }
        const quote = (j.quotes || []).find(q => q.id === quoteId);
        if (!quote) {
          return j;
        }
        const escrow = { ...(j.escrow || { status: 'none' }) };
        const messages = [
          ...(j.messages || []),
          systemMessage(
            `Accepted quote from ${quote.providerName} · ${quote.amount} ${quote.currency}.`
          ),
        ];
        // Prefill escrow amount from accepted quote when empty
        if (escrow.status !== 'none' && !escrow.amount) {
          escrow.amount = quote.amount;
          escrow.currency = quote.currency;
        }
        if (escrow.milestone === 'full' && escrow.status === 'offered') {
          // prompt lock in UI — leave offered
          messages.push(systemMessage('Escrow: full amount ready to lock.'));
        } else if (escrow.milestone === 'deposit' && escrow.status === 'offered') {
          messages.push(systemMessage('Escrow: lock deposit when ready.'));
        }
        return {
          ...j,
          acceptedQuoteId: quoteId,
          providerId: quote.providerId,
          providerName: quote.providerName,
          status: 'accepted',
          escrow,
          match: j.match ? { ...j.match, closedNotified: true } : j.match,
          messages,
        };
      });
    },
    [patchJob]
  );

  /** Buyer simulates a provider claiming (first-claim mode) or locks a quote. */
  const claimQuote = useCallback(
    (jobId, quoteId) => {
      let ok = false;
      patchJob(jobId, j => {
        if (j.acceptedQuoteId) {
          return j;
        }
        if (!['matching', 'quoted', 'open'].includes(j.status)) {
          return j;
        }
        const quote = (j.quotes || []).find(q => q.id === quoteId);
        if (!quote) {
          return j;
        }
        ok = true;
        return {
          ...j,
          acceptedQuoteId: quoteId,
          providerId: quote.providerId,
          providerName: quote.providerName,
          status: 'accepted',
          messages: [
            ...(j.messages || []),
            systemMessage(`Claim locked: ${quote.providerName} · ${quote.amount} ${quote.currency}.`),
          ],
        };
      });
      return ok;
    },
    [patchJob]
  );

  const setSchedule = useCallback(
    (jobId, scheduleWindow) => {
      patchJob(jobId, j => ({
        ...j,
        scheduleWindow,
        status: j.status === 'accepted' || j.status === 'quoted' ? 'scheduled' : j.status,
        messages: [
          ...(j.messages || []),
          systemMessage(`Schedule set: ${scheduleWindow || 'TBD'}.`),
        ],
      }));
    },
    [patchJob]
  );

  const transitionJob = useCallback(
    (jobId, toStatus) => {
      let ok = false;
      patchJob(jobId, j => {
        if (!canTransition(j.status, toStatus)) {
          return j;
        }
        ok = true;
        const escrow = { ...(j.escrow || { status: 'none' }) };
        const messages = [
          ...(j.messages || []),
          systemMessage(`Status → ${JOB_STATUS_LABELS[toStatus] || toStatus}.`),
        ];
        // Auto-offer lock reminder on complete if escrow still offered
        if (toStatus === 'complete' && escrow.status === 'offered') {
          messages.push(systemMessage('Escrow still unlocked — lock before settle, or settle without escrow.'));
        }
        // Block settle if locked? No — settle can wait for release first in UI
        return {
          ...j,
          status: toStatus,
          escrow,
          messages,
        };
      });
      return ok;
    },
    [patchJob]
  );

  const configureEscrow = useCallback(
    (jobId, { enabled, milestone, amount, currency } = {}) => {
      patchJob(jobId, j => {
        if (['locked', 'released', 'refunded'].includes(j.escrow?.status)) {
          return j;
        }
        const escrow = {
          ...(j.escrow || {}),
          status: enabled === false ? 'none' : 'offered',
          milestone: milestone || j.escrow?.milestone || 'deposit',
          amount: amount != null ? String(amount) : j.escrow?.amount || '',
          currency: currency || j.escrow?.currency || 'JMD',
          pin: null,
          lockedAt: null,
          releasedAt: null,
          releaseMethod: null,
        };
        return {
          ...j,
          escrow,
          messages: [
            ...(j.messages || []),
            systemMessage(
              escrow.status === 'none'
                ? 'Escrow turned off for this job.'
                : `Escrow configured · ${escrow.milestone}` +
                    (escrow.amount ? ` · ${escrow.amount} ${escrow.currency}` : '')
            ),
          ],
        };
      });
    },
    [patchJob]
  );

  const lockEscrow = useCallback(
    (jobId, overrides = {}) => {
      let ok = false;
      patchJob(jobId, j => {
        const escrow = { ...(j.escrow || { status: 'none' }) };
        if (escrow.status === 'locked' || escrow.status === 'released' || escrow.status === 'refunded') {
          return j;
        }
        const quote = (j.quotes || []).find(q => q.id === j.acceptedQuoteId);
        const amount =
          overrides.amount ||
          escrow.amount ||
          quote?.amount ||
          '';
        if (!amount) {
          return j;
        }
        const pin = generateHandoffPin();
        ok = true;
        return {
          ...j,
          escrow: {
            ...escrow,
            status: 'locked',
            milestone: overrides.milestone || escrow.milestone || 'deposit',
            amount: String(amount),
            currency: overrides.currency || escrow.currency || quote?.currency || 'JMD',
            pin,
            lockedAt: new Date().toISOString(),
            releasedAt: null,
            releaseMethod: null,
          },
          messages: [
            ...(j.messages || []),
            systemMessage(
              `Escrow locked · ${amount} ${
                overrides.currency || escrow.currency || quote?.currency || 'JMD'
              } · handoff PIN ${pin} (demo — keys stay with you).`
            ),
          ],
        };
      });
      return ok;
    },
    [patchJob]
  );

  const releaseEscrow = useCallback(
    (jobId, { pin, method = 'confirm' } = {}) => {
      let ok = false;
      patchJob(jobId, j => {
        const escrow = j.escrow;
        if (!escrow || escrow.status !== 'locked') {
          return j;
        }
        if (method === 'pin') {
          if (String(pin || '').trim() !== String(escrow.pin || '')) {
            return {
              ...j,
              messages: [
                ...(j.messages || []),
                systemMessage('Escrow release failed — PIN mismatch.'),
              ],
            };
          }
        }
        ok = true;
        return {
          ...j,
          escrow: {
            ...escrow,
            status: 'released',
            releasedAt: new Date().toISOString(),
            releaseMethod: method,
          },
          messages: [
            ...(j.messages || []),
            systemMessage(
              method === 'pin'
                ? 'Escrow released via handoff PIN.'
                : 'Escrow released on buyer confirm.'
            ),
          ],
        };
      });
      return ok;
    },
    [patchJob]
  );

  const refundEscrow = useCallback(
    jobId => {
      let ok = false;
      patchJob(jobId, j => {
        const escrow = j.escrow;
        if (!escrow || !['locked', 'offered'].includes(escrow.status)) {
          return j;
        }
        ok = true;
        return {
          ...j,
          escrow: {
            ...escrow,
            status: 'refunded',
            releasedAt: new Date().toISOString(),
            releaseMethod: 'refund',
          },
          messages: [
            ...(j.messages || []),
            systemMessage('Escrow refunded to payer (demo).'),
          ],
        };
      });
      return ok;
    },
    [patchJob]
  );

  const addMessage = useCallback(
    (jobId, { author = 'buyer', body }) => {
      const text = String(body || '').trim();
      if (!text) {
        return;
      }
      patchJob(jobId, j => ({
        ...j,
        messages: [
          ...(j.messages || []),
          {
            id: newEntityId('msg'),
            author,
            body: text,
            createdAt: new Date().toISOString(),
          },
        ],
      }));
    },
    [patchJob]
  );

  const cancelJob = useCallback(
    (jobId, { reason, detail = '', kind = 'cancel' } = {}) => {
      let ok = false;
      patchJob(jobId, j => {
        if (['settled', 'cancelled'].includes(j.status)) {
          return j;
        }
        ok = true;
        const escrow = { ...(j.escrow || {}) };
        const messages = [
          ...(j.messages || []),
          systemMessage(
            kind === 'no_show'
              ? `Cancelled — no-show (${reason || 'unspecified'}).`
              : `Cancelled (${reason || 'unspecified'}).`
          ),
        ];
        if (escrow.status === 'locked') {
          escrow.status = 'refunded';
          escrow.releasedAt = new Date().toISOString();
          escrow.releaseMethod = 'refund';
          messages.push(systemMessage('Escrow auto-refunded on cancel.'));
        }
        return {
          ...j,
          status: 'cancelled',
          escrow,
          cancel: {
            kind,
            reason: reason || 'other',
            detail: String(detail || '').trim(),
            at: new Date().toISOString(),
          },
          messages,
        };
      });
      return ok;
    },
    [patchJob]
  );

  const openDispute = useCallback(
    (jobId, { reason, detail = '', evidenceNote = '' } = {}) => {
      let ok = false;
      patchJob(jobId, j => {
        if (!canTransition(j.status, 'disputed')) {
          return j;
        }
        ok = true;
        return {
          ...j,
          status: 'disputed',
          dispute: {
            reason: reason || 'other',
            detail: String(detail || '').trim(),
            evidenceNote: String(evidenceNote || '').trim(),
            at: new Date().toISOString(),
          },
          messages: [
            ...(j.messages || []),
            systemMessage(`Dispute opened: ${reason || 'other'}. Evidence noted for arbitration.`),
          ],
        };
      });
      return ok;
    },
    [patchJob]
  );

  const closeMatchWindow = useCallback(
    jobId => {
      patchJob(jobId, j => {
        if (j.status !== 'matching') {
          return j;
        }
        return {
          ...j,
          status: (j.quotes || []).length ? 'quoted' : 'open',
          match: j.match ? { ...j.match, endsAt: new Date().toISOString(), closedNotified: true } : j.match,
          messages: [...(j.messages || []), systemMessage('Match window closed early.')],
        };
      });
    },
    [patchJob]
  );

  /** Mark job as escalated to human ops (WhatsApp / Telegram concierge). */
  const flagConcierge = useCallback(
    (jobId, { channel = 'ops' } = {}) => {
      patchJob(jobId, j => {
        if (j.conciergeRequested) {
          return j;
        }
        return {
          ...j,
          conciergeRequested: true,
          conciergeChannel: channel,
          conciergeAt: new Date().toISOString(),
          messages: [
            ...(j.messages || []),
            systemMessage(
              `Concierge requested via ${channel} — ops will help find a match. Keep this thread for updates.`
            ),
          ],
        };
      });
    },
    [patchJob]
  );

  const removeJob = useCallback(
    id => {
      setJobs(prev => prev.filter(j => j.id !== id));
      dirtyIdsRef.current.delete(id);
      if (ownerId) {
        apiDeleteTabletJob(id).catch(err => {
          console.warn('tablet job delete failed', id, err);
        });
      }
    },
    [ownerId]
  );

  const value = useMemo(
    () => ({
      jobs,
      syncStatus,
      syncReady,
      createJob,
      getJob,
      listJobs,
      attachQuotes,
      acceptQuote,
      claimQuote,
      setSchedule,
      transitionJob,
      configureEscrow,
      lockEscrow,
      releaseEscrow,
      refundEscrow,
      addMessage,
      cancelJob,
      openDispute,
      closeMatchWindow,
      flagConcierge,
      removeJob,
      statusLabel: s => JOB_STATUS_LABELS[s] || s,
      escrowLabel: s => ESCROW_STATUS_LABELS[s] || s,
    }),
    [
      jobs,
      syncStatus,
      syncReady,
      createJob,
      getJob,
      listJobs,
      attachQuotes,
      acceptQuote,
      claimQuote,
      setSchedule,
      transitionJob,
      configureEscrow,
      lockEscrow,
      releaseEscrow,
      refundEscrow,
      addMessage,
      cancelJob,
      openDispute,
      closeMatchWindow,
      flagConcierge,
      removeJob,
    ]
  );

  return <JobBoardContext.Provider value={value}>{children}</JobBoardContext.Provider>;
};

export const useJobBoard = () => useContext(JobBoardContext);

export default JobBoardContext;
